import {hash, isThenable} from './custom-utils';

const CACHE_KEY_PREFIX = 'cache-';

function isDefined(a: any): boolean {
    return typeof a !== 'undefined' && a !== null;
}

interface CacheKey {
    complex: string;
    simple: string;
}

// TODO: Add logging
// TODO: Add check if argument is function -> throw error
/**
 * @module cache
 * @name cacheable
 * @kind function
 *
 * @param {Storage=} [storage=window.localStorage] Specify cache storage. By default is `window.localStorage`.
 * @param {string=} [cacheName='DEFAULT'] Optional cache name for separating caches. Default is `DEFAULT`.
 *
 * @description
 * Provides caching feature on decorated method.
 */
export function cacheable(storage: Storage = window.localStorage, cacheName: string = 'DEFAULT') {
    const cache: Cache = Cache.getInstance(storage, cacheName);

    return (target: unknown, name: string, descriptor: TypedPropertyDescriptor<(...args: any[]) => any>) => {
        const original = descriptor.value;
        let isPromiseReturning = false;

        if (typeof original === 'function') {
            descriptor.value = function decoratorFn(...args) {
                let result;

                if (cache.hasItem(args)) {
                    result = cache.getItem(args);
                } else {
                    result = original.apply(this, args);

                    if (isThenable(result)) {

                        isPromiseReturning = true;
                        return result.then((value) => {
                            cache.setItem(args, value);
                            return value;
                        });

                    } else {
                        cache.setItem(args, result);
                    }
                }

                if (isPromiseReturning) {
                    return Promise.resolve(result);
                } else {
                    return result;
                }
            };
        }

        return descriptor;
    };
}

// TODO: Add storage overflow error checking, cache expiration
/**
 * @module cache
 * @name Cache
 * @kind class
 *
 * @hideconstructor
 *
 * @description
 * Class, that provides cache functions.
 * It has private constructor and instance of cache can be acquired by static method `getInstance`.
 * Cache is only layer on storage, which must be provided in getInstance method.
 */
export class Cache {

    /**
     * @description
     * List of all created caches.
     */
    private static caches: Cache[] = [];

    private storage: Storage;

    private cacheName: string;

    /**
     * @description
     * Cached keys =D
     */
    private keys: Set<string>;

    /**
     * @param {Storage} storage Cache storage
     * @param {string} cacheName Cache name
     *
     * @returns {Cache}
     *
     * @throws {CacheError} Invalid storage
     * @throws {CacheError} Cache name is not defined
     *
     * @description
     * Returns cache instance
     */
    public static getInstance(storage: Storage, cacheName: string): Cache {
        if (!storage || !Cache.validateStorage(storage)) {
            throw new CacheError(`Given storage is not valid. Use some object with Storage interface: ${JSON.stringify(storage)}`);
        }

        if (typeof cacheName !== 'string' || !cacheName) {
            throw new CacheError(`cacheName is required: ${cacheName}`);
        }

        for (const cache of Cache.caches) {
            if (cache.storage === storage && cache.cacheName === cacheName) {
                return cache;
            }
        }

        const newCache = new Cache(storage, cacheName);
        Cache.caches.push(newCache);

        return newCache;
    }

    /**
     * @param {Cache} cache
     *
     * @throws {CacheError} Undefined or null cache
     * @throws {CacheError} Given argument is not valid cache instance
     * @throws {CacheError} Cache not found
     *
     * @description
     * Remove all items and destroy given cache
     */
    public static destroyCache(cache: Cache) {
        if (!isDefined(cache)) {
            throw new CacheError('Cache::destroyCache - Undefined or null cache');
        }

        if (!(cache instanceof Cache)) {
            throw new CacheError('Cache::destroyCache - Given argument is not valid cache instance');
        }

        const index = Cache.caches.indexOf(cache);

        if (index < 0) {
            throw new CacheError(`Cache::destroyCache - Cache not found: ${JSON.stringify(cache)}`);
        }

        cache.clear();
        Cache.caches.splice(index, 1);
    }

    /**
     * @description
     * Removes all caches with its values from all storage.
     */
    public static purgeAllCaches() {
        for (const cache of Cache.caches) {
            Cache.destroyCache(cache);
        }
        Cache.caches = [];
    }

    /**
     * @param {Storage} storage Tested storage instance
     * @returns {boolean} Storage is valid / invalid
     *
     * @description
     * Validates if given object can be used as storage
     */
    private static validateStorage(storage: any): storage is Storage {
        return isDefined(storage.getItem) &&
            isDefined(storage.setItem) &&
            isDefined(storage.removeItem) &&
            isDefined(storage.clear) &&
            isDefined(storage.key) &&
            isDefined(storage.length);
    }

    /**
     * @param storage
     * @param cacheName
     */
    private constructor(storage: Storage, cacheName: string) {
        this.storage = storage;
        this.cacheName = cacheName;
        this.keys = new Set();

        this.tryToRecoverCache();
    }

    /**
     * @param {*} key Key
     * @param {*} value Cached value
     *
     * @description
     * Add item to cache
     */
    public setItem(key: any, value: any) {
        const internalKey = this.buildKeyFromObject(key);

        const stringifiedValue = JSON.stringify(value);

        this.keys.add(internalKey.simple);
        this.storage.setItem(internalKey.complex, stringifiedValue);
    }

    /**
     * @param {*} key Key
     * @returns {*} Cached value
     * @throws Error No item for given key found
     *
     * @description
     * Returns cached value by provided key. If no value found for this key, error is thrown.
     */
    public getItem(key: any): any {
        if (!this.hasItem(key)) {
            throw new Error(`Cache: No item found in cache ${this.cacheName}. Key is: ${key}`);
        }

        const internalKey = this.buildKeyFromObject(key);

        return JSON.parse(this.storage.getItem(internalKey.complex));
    }

    /**
     * @param {*} key Key
     * @returns {boolean} Returns true if cache has cached value for key.
     *
     * @description
     * Returns true if cache has cached value for key.
     */
    public hasItem(key: any): boolean {
        const internalKey = this.buildKeyFromObject(key);
        return this.keys.has(internalKey.simple);
    }

    /**
     * @param {*} key Key
     * @return {*} Removed value
     * @description
     * Removes item from cache and returns it.
     */
    public removeItem(key: any): any {
        const value = this.getItem(key);
        const internalKey = this.buildKeyFromObject(key);

        this.keys.delete(internalKey.simple);
        this.storage.removeItem(internalKey.complex);

        return value;
    }

    /**
     * @description
     * Clear entire cache, which means delete all cached items
     */
    public clear() {
        for (const key of this.keys) {
            this.storage.removeItem(this.buildComplexKey(key));
            this.keys.delete(key);
        }
    }

    /**
     * @description
     * Clear entire cache and destroy it
     */
    public destroyCache() {
        Cache.destroyCache(this);
    }

    /**
     * @param {string} simpleKey Simple key
     *
     * @returns {string} Complex key
     *
     * @description
     * Build complex key from given simpleKey
     */
    private buildComplexKey(simpleKey: string): string {
        return `${CACHE_KEY_PREFIX}-${this.cacheName}-${simpleKey}`;
    }

    /**
     * @description
     * Try to recover cache from defined storage. If storage contains cached values described by cache name, it fills keys set with values from cache.
     */
    private tryToRecoverCache() {
        const regexPattern = new RegExp(`^${CACHE_KEY_PREFIX}-${this.cacheName}-(.*)`);

        for (let i = 0; i < this.storage.length; i++) {
            const key = this.storage.key(i);

            if (regexPattern.test(key)) {
                const simpleKey = regexPattern.exec(key);
                if (simpleKey && simpleKey[1]) {
                    this.keys.add(simpleKey[1]);
                } else {
                    // This should not happen
                    throw new CacheError('Error restoring cache from storage. Invalid key.');
                }
            }
        }
    }

    /**
     * @param {*=} object Object from which will be generated key
     *
     * @returns {{complex: string, simple: string}} Key built from given object and cacheName
     *
     * @description
     * Generates simple and complex key from given object
     */
    private buildKeyFromObject(object?: any): CacheKey {
        const objectHash = hash(JSON.stringify(object));
        return {
            complex: this.buildComplexKey(objectHash),
            simple: objectHash
        };
    }
}

 
export class CacheError extends Error {

    constructor(message) {
        super(message);
        this.message = message;
        this.name = 'CacheError';
    }
}

// TODO: Add IndexedDB storage adapter
// TODO: Add in-memory storage adapter
